import { Link } from "@tanstack/react-router";
import { useEffect, useState, useSyncExternalStore } from "react";
import { Menu, X } from "lucide-react";
import { BrandLogo } from "./BrandLogo";
import { leerEscenaPlena, leerEscenaPlenaServidor, suscribirEscenaPlena } from "@/lib/escena-plena";

const NAV = [
  { to: "/", label: "Inicio" },
  { to: "/bodas", label: "Bodas" },
  { to: "/eventos", label: "Eventos" },
  { to: "/public", label: "Instituciones" },
  { to: "/contacto", label: "Contacto" },
];

type SiteHeaderProps = {
  /**
   * La cabecera flota sobre un hero a sangre: no ocupa sitio en el flujo y se
   * dibuja en hueso hasta que empieza el scroll. Se usa en la portada.
   */
  overlay?: boolean;
};

export function SiteHeader({ overlay = false }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Una sección a sangre (el scroll de bodas) avisa cuando ocupa la ventana.
  const escenaPlena = useSyncExternalStore(
    suscribirEscenaPlena,
    leerEscenaPlena,
    leerEscenaPlenaServidor,
  );

  // Sobre imagen oscura a sangre el texto va en hueso y la barra no tiene fondo.
  // En cuanto deja de haberla, vuelve al color de texto normal.
  const sobreImagen = (overlay && !scrolled) || escenaPlena;
  const onPhoto = sobreImagen && !open;
  const solid = (scrolled || open) && !onPhoto;

  return (
    <header
      className={`z-40 w-full transition-[background-color,border-color] duration-300 ${
        overlay ? "fixed top-0 left-0 right-0" : "sticky top-0"
      } ${solid ? "border-b border-border bg-background/85 backdrop-blur-xl" : "border-b border-transparent bg-transparent"}`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:h-20 lg:px-8">
        <Link to="/" className="group" aria-label="SUSIEDAD · Inicio">
          <BrandLogo className="h-5 w-auto transition-transform duration-300 group-hover:scale-[1.03] lg:h-6" />
        </Link>

        <nav className="hidden items-center gap-7 lg:flex">
          {NAV.slice(1).map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className={`text-sm font-medium transition-colors ${
                onPhoto
                  ? "text-bone/80 hover:text-bone"
                  : "text-foreground/75 hover:text-foreground"
              }`}
              activeProps={{ className: onPhoto ? "text-bone" : "text-foreground" }}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/acceso"
            className={`hidden text-sm font-medium transition-colors lg:inline-flex ${
              onPhoto ? "text-bone/80 hover:text-bone" : "text-foreground/75 hover:text-foreground"
            }`}
          >
            Acceso clientes
          </Link>
          {/* Sobre el hero el botón se calla: la portada ya lleva el suyo y dos
              botones rojos idénticos en el mismo pantallazo se anulan. Reaparece
              en cuanto el hero deja de estar delante. */}
          {!onPhoto && (
            <Link
              to="/contacto"
              className="hidden items-center bg-primary-strong px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.18em] text-primary-foreground transition-[background-color,transform] duration-200 hover:bg-[#d1001f] active:translate-y-px lg:inline-flex"
            >
              Consultar disponibilidad
            </Link>
          )}
          <button
            onClick={() => setOpen((v) => !v)}
            className={`inline-flex size-10 items-center justify-center transition-colors lg:hidden ${
              onPhoto ? "text-bone" : "text-foreground"
            }`}
            aria-label={open ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={open}
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border bg-background lg:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col gap-1 px-5 py-6">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="px-3 py-3 text-base font-medium transition-colors hover:bg-secondary"
              >
                {n.label}
              </Link>
            ))}
            <Link
              to="/acceso"
              onClick={() => setOpen(false)}
              className="px-3 py-3 text-base font-medium transition-colors hover:bg-secondary"
            >
              Acceso clientes
            </Link>
            <Link
              to="/contacto"
              onClick={() => setOpen(false)}
              className="mt-3 bg-primary-strong px-4 py-3 text-center text-xs font-semibold uppercase tracking-[0.18em] text-primary-foreground"
            >
              Consultar disponibilidad
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
